/**
 * The evaluation mark.
 *
 * This is the only thing an unlicensed BroadPaper does differently. Every
 * block, both PDF backends, the designer, data binding, pagination and the
 * render service behave identically with and without a licence; what changes is
 * that the file says, on every page, that it was produced with an evaluation
 * copy.
 *
 * It is drawn to be unmistakable in a document review and harmless to a
 * developer evaluating the product: a light diagonal line across the page,
 * behind nothing and over everything, at an opacity that leaves the report
 * entirely readable. Making the output illegible would only push people towards
 * evaluating something else.
 *
 * Each backend draws the spec with its own primitives — the Forme engine has a
 * page-native watermark node, Chromium gets a printed overlay — because a
 * single drawing implementation would mean one of them post-processing a file
 * the other had already finished, and a stamping pass that can be skipped is a
 * stamping pass that eventually is.
 */
import { resolveLicenseStatus, type ResolveOptions } from "./config.js";
import type { LicenseStatus } from "./types.js";

export interface WatermarkSpec {
  text: string;
  /** Points. The engines size the mark from this, not from the page. */
  fontSize: number;
  /** Degrees, anticlockwise from horizontal. */
  angle: number;
  /** Hex, without alpha. */
  color: string;
  /** 0–1. */
  opacity: number;
  /**
   * Where on the page it goes.
   *
   * `center` is the evaluation mark's diagonal stamp: sized from `fontSize`,
   * rotated by `angle`, across the middle of every page. It is the default and
   * the only thing `watermarkFor` ever asks for.
   *
   * `bottom` is a credit line along the foot of the page, in the bottom margin —
   * the shape a host's own commercial model usually wants. BroadPaper Cloud's
   * free plan started as a `center` mark at eight points, which is a small grey
   * sentence floating in the middle of an otherwise finished invoice: it reads
   * as a mistake rather than as a credit, and the whole point of that mark is
   * that a free user's document stays sendable.
   *
   * It is drawn in the margin rather than in the footer band deliberately, so it
   * costs no layout: a mark that changed the content height would make the
   * finished file paginate differently from the canvas the user approved.
   */
  placement?: "center" | "bottom";
  /**
   * A small mark drawn before the text, as an `image/svg+xml` data URI. `bottom`
   * only.
   *
   * Vector rather than a bitmap because it is drawn at about nine points and a
   * raster at that size is a smudge. Both backends parse the URI themselves —
   * the Forme engine's image decoder cannot read an SVG, so it is re-emitted as
   * the drawing it is.
   */
  mark?: string;
  /**
   * Where the text links to, making it a link in the file. `bottom` only, and
   * the Forme backend only — the Chromium backend stamps text into a finished
   * file and draws it unlinked.
   *
   * For a host's own credit line, whose job is to be followed: a free document a
   * business sends a client is the most-read advert the host will ever have, and
   * an address nobody can click is one hardly anybody types. The evaluation
   * stamp never has one, because it is there to be seen, not visited; its
   * footnote does, because saying where to get a key is the footnote's whole job.
   */
  href?: string;
  /**
   * A second mark drawn with this one: a `bottom` line under a `center` stamp.
   *
   * The evaluation mark carries one that says how to remove it. It is a field
   * of the spec rather than a second return value of `watermarkFor` so that
   * everything already passing one spec around — Cloud, a host's own extra mark,
   * both backends — carries it without learning a new shape. Backends reach it
   * through {@link expandWatermarks}.
   */
  footnote?: WatermarkSpec;
}

/**
 * The marks to draw, with each spec's `footnote` beside it and the empty
 * slots dropped. What every backend iterates.
 */
export function expandWatermarks(marks: ReadonlyArray<WatermarkSpec | null | undefined>): WatermarkSpec[] {
  const out: WatermarkSpec[] = [];
  for (const mark of marks) {
    if (!mark) continue;
    out.push(mark);
    if (mark.footnote) out.push(mark.footnote);
  }
  return out;
}

/**
 * The mark itself.
 *
 * No dashes or typographic punctuation: the standard PDF faces carry no em
 * dash, and a theme that names a font the engine does not have falls back to
 * them, which turned "BroadPaper — Evaluation Copy" into "BroadPaper
 * Evaluation Copy" in half the documents and a question mark in the rest.
 */
export const EVALUATION_WATERMARK: WatermarkSpec = {
  text: "BroadPaper Evaluation Copy",
  fontSize: 44,
  angle: -35,
  color: "#c8102e",
  opacity: 0.12,
  /*
   * The line that says what to do about the stamp. The stamp itself is never a
   * link — it is there to be seen. This line is the opposite: it exists to be
   * followed, so the Forme backend links it; Chromium stamps text into a finished
   * file and draws it unlinked. Darker than the stamp because at eight points
   * the stamp's opacity is unreadable, and still grey enough to leave the page
   * a page. A full stop rather than a dash, for the reason above.
   */
  footnote: {
    text: "Evaluation copy. Remove this mark with a free key: broadpaper.com/eval",
    fontSize: 8,
    angle: 0,
    color: "#6b6b6b",
    opacity: 0.9,
    placement: "bottom",
    href: "https://www.broadpaper.com/eval"
  }
};

/**
 * The mark a render should carry, or null when the licence says none.
 *
 * Backends call this rather than reading the licence status themselves, so
 * "licensed" and "no watermark" cannot drift apart.
 */
export async function resolveWatermark(options: ResolveOptions = {}): Promise<WatermarkSpec | null> {
  const status = await resolveLicenseStatus(options);
  return watermarkFor(status);
}

/** The pure half of {@link resolveWatermark}, for callers that already have a status. */
export function watermarkFor(status: LicenseStatus): WatermarkSpec | null {
  return status.licensed ? null : EVALUATION_WATERMARK;
}

/**
 * CSS that paints the mark on every `.bp-page` of a rendered document.
 *
 * Used by the Chromium backend and by the designer's print path — the two
 * places a PDF is produced by a browser rather than by the engine. `position:
 * fixed` would put it on the viewport rather than on each page, so it is an
 * absolutely positioned pseudo-element on the page box, which prints once per
 * sheet.
 *
 * `print-color-adjust` matters: without it Chromium drops the mark from the
 * printed file exactly as it drops any other background colour.
 */
export function watermarkCss(spec: WatermarkSpec = EVALUATION_WATERMARK): string {
  const note = spec.footnote;
  return [
    ".bp-page{position:relative;}",
    ".bp-page::after{",
    `content:"${cssString(spec.text)}";`,
    "position:absolute;inset:0;",
    "display:flex;align-items:center;justify-content:center;",
    "pointer-events:none;z-index:2147483647;",
    `transform:rotate(${spec.angle}deg);`,
    "font-family:Helvetica,Arial,sans-serif;font-weight:700;letter-spacing:0.04em;white-space:nowrap;",
    `font-size:${spec.fontSize}pt;color:${spec.color};opacity:${spec.opacity};`,
    "-webkit-print-color-adjust:exact;print-color-adjust:exact;",
    "}",
    // The footnote, along the foot of the same page box. Unlinked here: a
    // pseudo-element cannot be an anchor.
    ...(note
      ? [
          ".bp-page::before{",
          `content:"${cssString(note.text)}";`,
          "position:absolute;left:0;right:0;bottom:4pt;text-align:center;",
          "pointer-events:none;z-index:2147483647;",
          "font-family:Helvetica,Arial,sans-serif;white-space:nowrap;",
          `font-size:${note.fontSize}pt;color:${note.color};opacity:${note.opacity};`,
          "-webkit-print-color-adjust:exact;print-color-adjust:exact;",
          "}"
        ]
      : [])
  ].join("");
}

function cssString(text: string): string {
  return text.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}

/**
 * The PDF Producer string for a render.
 *
 * Licensed output records the licence id. It is not a secret — it identifies a
 * customer's licence, not their account — and having it in the file means a
 * document that turns up somewhere it should not can be traced back to the
 * licence that produced it without any of the tracking a phone-home would need.
 */
export function producerString(status: LicenseStatus): string {
  if (!status.licensed || !status.payload) return "BroadPaper (evaluation)";
  return `BroadPaper (${status.payload.licenseId})`;
}
